/**
 * License Extension (LICEXTAGENT) renderer models.
 *
 * Ported from the legacy wg-agent-hub-ui licextagent.model.ts. These describe
 * the inline-wizard payload the runtime emits as the whitelisted `licext_form`
 * SSE event, plus the per-message UI state the renderer keeps.
 *
 * Isolated to the licextagent feature — nothing here touches the shared chat
 * model except via the host's ChatMessage extension fields (licextForm, etc.).
 */

export type LicExtFlowType = 'device' | 'saas';

// ── Inline wizard form payload — from the show_extension_form / *_form tools ──

/** Step-1 device row — serial only (flow_type='device_step1').
 *  `invalid`/`error` are set by the agent when a serial was not found, not in
 *  the account system, or has no applicable extension SKUs, so the form can
 *  highlight that row and show the message. */
export interface FormDataDeviceRow {
  serial?: string;
  invalid?: boolean;
  error?: string;
}

/** A device extension SKU option — from the LEMS extensionSkus API. */
export interface FormDataDeviceSku {
  sku?: string;
  term?: string;
}

/** Step-2 device row — serial + account ID (frozen) + applicable SKUs list.
 *  `selected_sku` is set only when the agent re-opens the form to preserve a SKU
 *  the user had already picked (reload / "change the reason" etc.). */
export interface FormDataDeviceStep2Row {
  serial_number?: string;
  account_id?: string;
  extension_skus?: FormDataDeviceSku[];
  selected_sku?: string;
}

/** Step-1 SaaS row — container key only (flow_type='saas_step1'). */
export interface FormDataSaasStep1Row {
  container_key?: string;
}

/** Step-2 SaaS product item — from the Entitlement API
 *  (GET /api/v2/Entitlement/{activationKey}). `expiration_date` is the product's
 *  CURRENT end date and `product_activation_key` identifies the product; the
 *  renderer threads both into the step-2 seed (the backend computes the new
 *  expiry as current + days). */
/** Allowed SaaS extension lengths. The agent sends the live list as `day_options`. */
export type SaasDays = 3 | 7 | 14 | 30;
export const DEFAULT_SAAS_DAY_OPTIONS: SaasDays[] = [3, 7, 14, 30];

export interface FormDataSaasProduct {
  entitlement_id?: string;
  product_name?: string;
  expiration_date?: string;
  product_activation_key?: string | null;
  sku?: string;
  /** Set only when the agent re-opens the form to preserve a previously chosen
   *  extension length for this product (reload / "change the reason" etc.). */
  selected_days?: SaasDays;
}

/** Step-2 SaaS row — container key + account ID (frozen) + products list. */
export interface FormDataSaasStep2Row {
  container_key?: string;
  account_id?: string;
  products?: FormDataSaasProduct[];
}

/** One selectable approver in the approver-selection wizard. `region` is the
 *  approver's config-bucket region — used for the region filter and search, and
 *  threaded back so the backend can record per-region approver stats. */
export interface FormDataApprover {
  name?: string;
  email?: string;
  /** Display string — all of the approver's regions joined with ", ". */
  region?: string;
  /** Every region the approver is configured in; the region filter matches any. */
  regions?: string[];
}

export type FormData =
  // Device-or-SaaS chooser — renders two quick-reply pills under the agent's
  // "which type?" message (no rows; clicking a pill sends the choice as a
  // normal user message).
  | { flow_type: 'type_choice' }
  | { flow_type: 'device_step1'; device_rows?: FormDataDeviceRow[] }
  | { flow_type: 'device_step2'; device_step2_rows?: FormDataDeviceStep2Row[]; reason?: string; account_name?: string }
  | { flow_type: 'saas_step1'; saas_rows?: FormDataSaasStep1Row[]; allowed_skus?: string[] }
  | { flow_type: 'saas_step2'; saas_step2_rows?: FormDataSaasStep2Row[]; allowed_skus?: string[]; reason?: string; account_name?: string; day_options?: number[] }
  // Approver-selection wizard — shown only when the agent's approval pre-check
  // flagged at least one item as needing approval. The user picks ≥1 approver
  // from the cross-region list; the renderer composes a "Selected approvers:" seed.
  // `selected_emails` pre-selects approvers when the form is re-opened.
  | { flow_type: 'select_approvers'; approvers?: FormDataApprover[]; selected_emails?: string[] };

// ── UI-level state ──────────────────────────────────────────────────────────

/** UI device step-1 row (editable serial; invalid/error flagged by the agent). */
export interface DeviceStep1UiRow {
  serial: string;
  invalid: boolean;
  error?: string;
}

/** UI device step-2 row — frozen serial/account + its applicable SKUs + the
 *  chosen SKU + the per-row dropdown panel state. */
export interface DeviceStep2UiRow {
  serial: string;
  accountId: string;
  skus: FormDataDeviceSku[];
  selectedSku: string | null;
  panelOpen: boolean;
}

/** UI SaaS step-1 row (editable container key). */
export interface SaasStep1UiRow {
  containerKey: string;
}

/** A product selected in step-2, with the chosen extension length. */
export interface SaasSelectedProduct {
  product: FormDataSaasProduct;
  days: SaasDays | null;
  containerKey: string;
  accountId: string;
}

/** UI step-2 row — frozen container/account + its products + the panel state. */
export interface SaasStep2UiRow {
  containerKey: string;
  accountId: string;
  products: FormDataSaasProduct[];
  selected: Record<number, SaasDays>; // product index → chosen days
  panelOpen: boolean;
}

/** Set on the agent message when the submit flow completes. The counts let the
 *  success card phrase the notification line accurately (auto-processed vs
 *  sent-for-approval vs both). */
export interface LicextFlowComplete {
  flow_type: LicExtFlowType;
  request_id: string;
  direct_count?: number;
  pending_count?: number;
}

/** Action emitted up to the host (agenthome-chat). The renderer composes the
 *  seed message itself; the host only sends it and marks the form submitted. */
export interface LicextAction {
  type: 'submit';
  payload: string;   // the seed message text
  msgIdx: number;
}

/** The subset of the host ChatMessage the renderer reads. The host's
 *  ChatMessage includes these optional fields (additive — no existing field
 *  is touched). */
export interface LicextChatMessage {
  licextForm?: FormData;
  licextFormSubmitted?: boolean;
  licextFlowComplete?: LicextFlowComplete;
}
